import React, { useEffect, useRef, useState } from 'react';

/**
 * The waiting game. Resume generation takes a minute or two, so the wait
 * screen offers a Chrome-dino-style runner: press Space (or tap) to start,
 * Space to jump, dodge the REJECTED stamps. Score is survival time; best
 * score persists locally. Self-contained canvas, no dependencies, and the
 * parent simply unmounts it the moment the resume is ready.
 */
export default function RunnerGame() {
  const canvasRef = useRef(null);
  const stateRef = useRef(null);
  const [best, setBest] = useState(() => Number(localStorage.getItem('runnerBest') || 0));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;
    const GROUND = H - 24;

    const s = {
      mode: 'idle',              // idle | running | dead
      y: GROUND,                 // runner's feet
      vy: 0,
      obstacles: [],             // {x, w, h}
      speed: 4.2,
      t: 0,                      // frames survived
      nextGap: 60,
      raf: 0,
    };
    stateRef.current = s;

    const reset = () => {
      s.y = GROUND; s.vy = 0; s.obstacles = []; s.speed = 4.2; s.t = 0; s.nextGap = 60;
    };

    const jump = () => {
      if (s.mode !== 'running') {
        reset();
        s.mode = 'running';
        return;
      }
      if (s.y >= GROUND - 0.5) s.vy = -11.5;
    };

    const onKey = (e) => {
      if (e.code === 'Space' || e.code === 'ArrowUp') {
        e.preventDefault();
        jump();
      }
    };
    const onTap = (e) => { e.preventDefault(); jump(); };
    window.addEventListener('keydown', onKey);
    canvas.addEventListener('pointerdown', onTap);

    const RUNNER_X = 46;
    const RUNNER_W = 22;
    const RUNNER_H = 30;

    const draw = () => {
      ctx.clearRect(0, 0, W, H);

      // ground
      ctx.strokeStyle = '#cbd5e1';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, GROUND + 1);
      ctx.lineTo(W, GROUND + 1);
      ctx.stroke();

      // runner: a little document with legs, the resume on its way
      const ry = s.y - RUNNER_H;
      ctx.fillStyle = '#2563EB';
      ctx.fillRect(RUNNER_X, ry, RUNNER_W, RUNNER_H);
      ctx.fillStyle = '#ffffff';
      for (let i = 0; i < 3; i++) {
        ctx.fillRect(RUNNER_X + 4, ry + 6 + i * 7, RUNNER_W - 8, 2.5);
      }
      // legs alternate while running
      ctx.fillStyle = '#2563EB';
      const stride = s.mode === 'running' && s.y >= GROUND - 0.5 ? (Math.floor(s.t / 6) % 2 ? 4 : -4) : 0;
      ctx.fillRect(RUNNER_X + 4 + stride, s.y - 1, 4, 5);
      ctx.fillRect(RUNNER_X + RUNNER_W - 8 - stride, s.y - 1, 4, 5);

      // obstacles: REJECTED stamps
      for (const o of s.obstacles) {
        ctx.fillStyle = '#DC2626';
        ctx.fillRect(o.x, GROUND - o.h, o.w, o.h);
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 7px system-ui, sans-serif';
        ctx.save();
        ctx.translate(o.x + o.w / 2, GROUND - o.h / 2);
        ctx.rotate(-0.22);
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('REJECTED', 0, 0);
        ctx.restore();
      }

      // score
      ctx.fillStyle = '#64748b';
      ctx.font = '12px ui-monospace, Menlo, monospace';
      ctx.textAlign = 'right';
      const score = Math.floor(s.t / 6);
      const bestNow = Number(localStorage.getItem('runnerBest') || 0);
      ctx.fillText(`${score}  ·  best ${Math.max(bestNow, score)}`, W - 10, 18);

      // prompts
      ctx.textAlign = 'center';
      if (s.mode === 'idle') {
        ctx.fillStyle = '#334155';
        ctx.font = '600 14px system-ui, sans-serif';
        ctx.fillText('Press Space or tap to play while you wait', W / 2, H / 2 - 8);
        ctx.fillStyle = '#94a3b8';
        ctx.font = '12px system-ui, sans-serif';
        ctx.fillText('Jump the rejections. Your resume keeps building either way.', W / 2, H / 2 + 12);
      } else if (s.mode === 'dead') {
        ctx.fillStyle = '#334155';
        ctx.font = '600 14px system-ui, sans-serif';
        ctx.fillText('Rejected! Press Space to run it back.', W / 2, H / 2 - 4);
      }
    };

    const tick = () => {
      if (s.mode === 'running') {
        s.t += 1;
        s.speed = Math.min(9, 4.2 + s.t / 900);
        // physics
        s.vy += 0.6;
        s.y = Math.min(GROUND, s.y + s.vy);

        // spawn
        s.nextGap -= 1;
        if (s.nextGap <= 0) {
          const h = 18 + Math.floor((s.t % 37) / 37 * 14) + ((s.t % 3) * 4);
          s.obstacles.push({ x: W + 10, w: 34, h: Math.min(34, h) });
          s.nextGap = 55 + ((s.t * 7) % 70);
        }
        // move + collide
        const ry = s.y - RUNNER_H;
        for (const o of s.obstacles) {
          o.x -= s.speed;
          const overlapX = o.x < RUNNER_X + RUNNER_W - 4 && o.x + o.w > RUNNER_X + 4;
          const overlapY = s.y > GROUND - o.h + 2 && ry < GROUND;
          if (overlapX && overlapY) {
            s.mode = 'dead';
            const score = Math.floor(s.t / 6);
            const prev = Number(localStorage.getItem('runnerBest') || 0);
            if (score > prev) {
              localStorage.setItem('runnerBest', String(score));
              setBest(score);
            }
          }
        }
        s.obstacles = s.obstacles.filter((o) => o.x + o.w > -10);
      }
      draw();
      s.raf = requestAnimationFrame(tick);
    };
    s.raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(s.raf);
      window.removeEventListener('keydown', onKey);
      canvas.removeEventListener('pointerdown', onTap);
    };
  }, []);

  return (
    <div className="w-full max-w-xl mx-auto">
      <canvas
        ref={canvasRef}
        width={560}
        height={170}
        className="w-full bg-white border border-gray-200 rounded-xl shadow-sm touch-none select-none"
        aria-label="A small runner game to pass the time. Press Space to start and jump."
      />
      {best > 0 && (
        <p className="text-center text-[11px] text-gray-400 mt-1.5">Best run: {best}</p>
      )}
    </div>
  );
}
